import type { TFunction } from "i18next";
import { useRef, type KeyboardEvent } from "react";

export const MAX_GRADE = 10;
export const GRADES = Array.from({ length: MAX_GRADE + 1 }, (_, grade) => grade);

const TEN_WINDOW_MS = 700;

export const ARROWS = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];

// "Fair" and "Moderate" carry «Under middels» and «Middels»: the obvious English keys are taken by
// an unrelated enum mapping in useTranslationTools, whose Norwegian says something else.
// t("Not met") t("Very weak") t("Weak") t("Fair") t("Moderate") t("Satisfactory")
// t("Good") t("Very good") t("Strong") t("Very strong") t("Excellent")
const GRADE_KEYS = [
  "Not met",
  "Very weak",
  "Weak",
  "Fair",
  "Moderate",
  "Satisfactory",
  "Good",
  "Very good",
  "Strong",
  "Very strong",
  "Excellent",
] as const;

export const gradeLabel = (t: TFunction, grade: number): string => String(t(GRADE_KEYS[grade]));

export const gradeValueText = (t: TFunction, value: number | null): string =>
  value == null
    ? String(t("Not given"))
    : `${value} ${t("of {{max}} - {{label}}", { max: MAX_GRADE, label: gradeLabel(t, value) })}`;

interface Options {
  value: number | null;
  onChange: (value: number | null) => void;
  isReadOnly?: boolean;
  /** Off where the control steps by itself, so an arrow is not acted on twice. */
  stepping?: boolean;
}

export const useGradeKeys = ({ value, onChange, isReadOnly = false, stepping = true }: Options) => {
  const lastDigit = useRef<{ digit: number; at: number } | null>(null);

  const select = (next: number) => {
    if (isReadOnly) return;
    if (next === value) return;
    onChange(next);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (isReadOnly) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const { key } = event;
    if (!/^[0-9]$/.test(key)) lastDigit.current = null;

    if (!stepping && (ARROWS.includes(key) || key === "Home" || key === "End")) return;

    if (key === "ArrowRight" || key === "ArrowUp") {
      event.preventDefault();
      select(value == null ? 0 : Math.min(MAX_GRADE, value + 1));
      return;
    }
    if (key === "ArrowLeft" || key === "ArrowDown") {
      event.preventDefault();
      select(value == null ? 0 : Math.max(0, value - 1));
      return;
    }
    if (key === "Home") {
      event.preventDefault();
      select(0);
      return;
    }
    if (key === "End") {
      event.preventDefault();
      select(MAX_GRADE);
      return;
    }
    if (key === "Backspace" || key === "Delete") {
      event.preventDefault();
      if (value != null) onChange(null);
      return;
    }
    if (/^[0-9]$/.test(key)) {
      event.preventDefault();
      const digit = Number(key);
      const previous = lastDigit.current;
      if (digit === 0 && previous?.digit === 1 && Date.now() - previous.at < TEN_WINDOW_MS) {
        lastDigit.current = null;
        select(MAX_GRADE);
        return;
      }
      lastDigit.current = { digit, at: Date.now() };
      select(digit);
    }
  };

  return { select, onKeyDown };
};
